<template>
  <!-- 商机列表：全部在跟商机。商机不允许删除，关闭商机请把阶段改为「已流失」 -->
  <div class="opportunity-list">
    <list-page>
      <search-condition @search="resetSearch" :show-search="true" page-name="opportunityList">
        <template #default>
          <el-input v-model="searchInfo.searchKey" placeholder="商机名称/编号" @keydown.enter="resetSearch" clearable>
            <template #prefix>商机名称</template>
          </el-input>
          <el-select v-model="searchInfo.currentStage" placeholder="全部(不显示成交和流失客户)" clearable>
            <template #prefix>当前阶段</template>
            <el-option v-for="s in stageList" :key="s.userCode" :label="s.dictName" :value="Number(s.userCode)"/>
          </el-select>
          <!-- 普通角色只看得到与自己相关的商机，按负责人再筛一次没有意义，直接不显示 -->
          <employee-select v-if="isSuperAdmin" v-model="searchInfo.ownerEmployeeId" placeholder="全部" @change="resetSearch">
            <template #prefix>负责人</template>
          </employee-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
          <page-button type="write" el-type="success" @click="openDialog()">+ 新建商机</page-button>
        </template>
      </search-condition>

      <list-table v-loading="loading" background-color="#FFF">
        <yq-table :data="dataList" :empty-text="'暂无数据'" height="100%" page-name="opportunityList">
          <yq-table-column label="商机编号" prop="opportunityNo" width="130rem"/>
          <yq-table-column label="商机名称" prop="opportunityTitle" min-width="200rem" :show-overflow-tooltip="true">
            <template v-slot="{row}">
              <span class="name-link" @click="openDetail(row)">{{ row.opportunityTitle }}</span>
            </template>
          </yq-table-column>
          <yq-table-column label="当前阶段" prop="stageTitle" align="center" width="110rem">
            <template v-slot="{row}">
              <el-tag size="small" :type="stageTagType(row.currentStage)">{{ stageName(row.currentStage) }}</el-tag>
            </template>
          </yq-table-column>
          <!-- 成交概率并入金额列，避免列过多横向溢出 -->
          <yq-table-column label="商机金额" prop="opportunityAmount" align="right" width="130rem">
            <template v-slot="{row}">
              <div class="cell-main"><span class="num bold">{{ money(row.opportunityAmount) }}</span></div>
              <div class="cell-sub">成交概率 {{ row.winRate || 0 }}%</div>
            </template>
          </yq-table-column>
          <yq-table-column label="预计成交" prop="expectDealDate" width="110rem"/>
          <yq-table-column label="负责人" prop="ownerEmployeeName" width="90rem"/>
          <yq-table-column label="来源" prop="sourceTitle" width="110rem">
            <template v-slot="{row}">
              <el-tag size="small" type="info" v-if="sourceName(row.sourceDictId)">{{ sourceName(row.sourceDictId) }}</el-tag>
            </template>
          </yq-table-column>
          <yq-table-column label="操作" prop="options" align="center" fixed="right" width="240" :show-overflow-tooltip="false">
            <template v-slot="{row}">
              <table-button @click="openFollowDialog(row)">跟进</table-button>
              <table-button @click="openDetail(row)">详情</table-button>
              <table-button @click="openDialog(row)">编辑</table-button>
              <table-button el-type="warning" message="确认将该商机转为客户？转换后它不再出现在商机列表，其跟进记录会归到客户名下" @click="convertToCustomer(row)">转客户</table-button>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>

      <form-page v-model:page-number="searchInfo.pageNumber" v-model:page-size="searchInfo.pageSize"
                 :total-count="searchInfo.totalCount" @onLoad="queryList"/>
    </list-page>

    <!-- 新建/编辑商机 -->
    <el-dialog v-model="showDialog" :title="dialogData.id ? '编辑商机' : '新建商机'" dialog-width="2" :close-on-click-modal="false">
      <form-items :data="dialogData" :columns="2" label-width="2.4rem" icon-width="0" ref="dialogForm">
        <form-item label="商机名称" :required="true" name="opportunityTitle" error-message="请填写商机名称" :show-block="true">
          <el-input v-model="dialogData.opportunityTitle" placeholder="如：某某公司年度采购商机"/>
        </form-item>
        <form-item label="商机阶段">
          <el-select v-model="dialogData.currentStage" placeholder="请选择" style="width: 100%">
            <el-option v-for="s in stageList" :key="s.userCode" :label="s.dictName" :value="Number(s.userCode)"/>
          </el-select>
        </form-item>
        <form-item label="商机金额(元)">
          <el-input-number v-model="dialogData.opportunityAmount" :min="0" :precision="2" :controls="false" style="width: 100%"/>
        </form-item>
        <form-item label="成交概率(%)">
          <el-input-number v-model="dialogData.winRate" :min="0" :max="100" :controls="false" style="width: 100%"/>
        </form-item>
        <form-item label="负责人" desc-message="排在第一位的是主负责人，其余是业务人员">
          <!-- 多选且可拖拽排序：排在第一位的是主要负责人，其余作为业务人员 -->
          <employee-select-sort v-model="dialogData.ownerEmployeeIds"/>
        </form-item>
        <form-item label="商机来源">
          <el-select v-model="dialogData.sourceDictId" placeholder="请选择" clearable style="width: 100%">
            <el-option v-for="s in sourceList" :key="s.id" :label="s.dictName" :value="s.userCode"/>
          </el-select>
        </form-item>
        <form-item label="预计成交">
          <el-date-picker v-model="dialogData.expectDealDate" type="date" value-format="YYYY-MM-DD" style="width: 100%"/>
        </form-item>
        <!-- 阶段选到「已流失」时必填输单原因 -->
        <form-item label="输单原因" v-if="isLostStage(dialogData.currentStage)" :show-block="true">
          <el-input v-model="dialogData.closeReason" type="textarea" :rows="2" placeholder="请说明输单原因"/>
        </form-item>
        <form-item label="备注" :show-block="true">
          <el-input v-model="dialogData.remark" type="textarea" :rows="2" placeholder="选填"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveData">保存</page-button>
      </template>
    </el-dialog>

    <!-- 跟进：商机列表行内「跟进」+「详情」共用 -->
    <el-dialog v-model="showFollowDialog" :title="followTitle" dialog-width="2" :close-on-click-modal="false">
      <div class="follow-detail" v-if="followList.length">
        <div class="follow-tip">跟进动态（{{ followList.length }} 条）</div>
        <div v-for="f in followList" :key="f.id" class="follow-item">
          <div class="follow-head">
            <span class="follow-who">{{ f.followEmployeeName || '—' }}</span>
            <span class="follow-stage" v-if="stageName(f.stageValue)">{{ stageName(f.stageValue) }}</span>
            <span class="follow-time">{{ f.followTime }}</span>
          </div>
          <div class="follow-content">{{ f.followContent }}</div>
        </div>
      </div>
      <form-items :data="followData" :columns="1" label-width="2.4rem" icon-width="0" ref="followForm">
        <form-item label="跟进内容" :required="true" name="followContent" error-message="请填写跟进内容" :show-block="true">
          <el-input v-model="followData.followContent" type="textarea" :rows="3" placeholder="本次沟通的情况"/>
        </form-item>
        <form-item label="商机阶段">
          <el-select v-model="followData.stageValue" placeholder="请选择" style="width: 100%">
            <el-option v-for="s in stageList" :key="s.userCode" :label="s.dictName" :value="Number(s.userCode)"/>
          </el-select>
        </form-item>
        <!-- 默认当天，可手动改。只取到日：跟进记录不记到时分，「今天」也就不会因打开弹窗的时刻而漂到昨天 -->
        <form-item label="跟进时间">
          <el-date-picker v-model="followData.followTime" type="date" value-format="YYYY-MM-DD" style="width: 100%"/>
        </form-item>
        <form-item label="下次跟进">
          <el-date-picker v-model="followData.nextFollowDate" type="date" value-format="YYYY-MM-DD" style="width: 100%"/>
        </form-item>
      </form-items>
      <template #footer>
        <page-button @click="showFollowDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveFollow">保存</page-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import TableButton from "@/components/h5/button/TableButton.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import EmployeeSelectSort from "@/components/h5/data/EmployeeSelectSort.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import opportunityApi from "@/api/dz-crm/partner/opportunityApi";
import {DICT_OPPORTUNITY_STAGE, DICT_PARTNER_ORIGIN, STAGE, dictName, stageTagType} from "@/views/h5/crm/crmDictionary";
import commonApi from "@/api/commonApi";
import Util from "@/util/util";
import {useRoute, useRouter} from "vue-router";

/** 取本地当天的 yyyy-MM-dd。不能用 toISOString()：它是 UTC，东八区凌晨会退到前一天 */
function today() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
}

// 阶段值 → 标签色的固定映射。字典只提供名称，颜色由前端决定。

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "OpportunityList",
  components: {
    YqTable,
    ListPage, ListTable, SearchCondition, FormPage, FormItems, FormItem,
    PageButton, TableButton, EmployeeSelect, EmployeeSelectSort,
  },
  data() {
    return {
      loading: false,
      searchInfo: {pageNumber: 1, pageSize: 20, searchKey: "", currentStage: "", ownerEmployeeId: ""},
      dataList: [],
      stageList: [],
      sourceList: [],
      showDialog: false,
      dialogData: {},
      showFollowDialog: false,
      followTitle: "商机跟进",
      followData: {},
      followList: [],
      // 角色分类，1超级管理员 2普通角色。普通角色只看得到与自己相关的商机
      roleType: "",
    };
  },
  computed: {
    isSuperAdmin() {
      return this.roleType === 1;
    },
  },
  beforeRouteLeave(to, from, next) {
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    this.roleType = Util.getRoleType();
    this.initDictionary();
    this.resetSearch();
  },
  methods: {
    initDictionary() {
      // 阶段：OPPORTUNITY_STAGE；来源：PARTNER_ORIGIN
      commonApi.queryUniqueDictionaryList(DICT_OPPORTUNITY_STAGE).then(list => this.stageList = list || []);
      commonApi.queryUniqueDictionaryList(DICT_PARTNER_ORIGIN).then(list => this.sourceList = list || []);
    },
    async queryList() {
      // 空值不提交，避免空串绑到后端 Integer 报错
      const params = {};
      Object.keys(this.searchInfo).forEach(k => {
        const v = this.searchInfo[k];
        if (v === "" || v === null || v === undefined) {
          return;
        }
        params[k] = v;
      });
      this.loading = true;
      try {
        const {records, totalCount} = await opportunityApi.queryList(params);
        this.dataList = records || [];
        this.searchInfo.totalCount = totalCount;
      } finally {
        this.loading = false;
      }
    },
    resetSearch() {
      this.searchInfo.pageNumber = 1;
      this.queryList();
    },
    /** 阶段名、来源名由字典关联 —— 后端只返回值，不返回名称 */
    stageName(stageValue) {
      return dictName(this.stageList, stageValue);
    },
    sourceName(sourceId) {
      return dictName(this.sourceList, sourceId);
    },
    stageTagType(stageValue) {
      return stageTagType(stageValue);
    },
    /** 是否已流失阶段（输单原因只在关闭时填） */
    isLostStage(stageValue) {
      return stageValue === STAGE.LOST;
    },
    money(value) {
      if (value === null || value === undefined) {
        return "—";
      }
      return Number(value).toLocaleString("zh-CN", {minimumFractionDigits: 2, maximumFractionDigits: 2});
    },
    openDialog(row) {
      if (row) {
        this.dialogData = JSON.parse(JSON.stringify(row));
        // ownerEmployeeIds 由列表接口一并返回（逗号分隔，第一位是主要负责人），
        // 这里只需保证非空，避免员工选择组件拿到 undefined
        this.dialogData.ownerEmployeeIds = this.dialogData.ownerEmployeeIds || "";
      } else {
        this.dialogData = {
          currentStage: STAGE.CLUE, winRate: 0, opportunityAmount: 0,
          // 新建时默认把当前登录人设为主要负责人：普通角色只看得到与自己相关的数据，
          // 留空会导致刚建完的商机在列表里查不到。异步获取用户信息会覆盖用户已改的表单，所以同步取。
          ownerEmployeeIds: Util.getEmployeeId(),
        };
      }
      this.showDialog = true;
    },
    async saveData() {
      if (!this.$refs.dialogForm.validCheck()) {
        return;
      }
      await opportunityApi.saveOpportunity(this.dialogData);
      this.showDialog = false;
      this.resetSearch();
    },
    openDetail(row) {
      this.router.push({path: "/opportunity/list/detail", query: {id: row.id}});
    },
    /** 行内「转客户」：只改对象类型，商机自己的档案与历史跟进都跟着走 */
    async convertToCustomer(row) {
      await opportunityApi.convertToCustomer(row.id);
      this.resetSearch();
    },
    /** 行内「跟进」：预填该商机的阶段、负责人，跟进时间默认当天（可改） */
    async openFollowDialog(row) {
      this.followTitle = "商机跟进 · " + row.opportunityTitle;
      this.followData = {
        // 跟进表按往来单位档案id归属，不是商机行主键 id
        partnerId: row.partnerId,
        stageValue: row.currentStage,
        followEmployeeId: row.ownerEmployeeId,
        followContent: "",
        followTime: today(),
        nextFollowDate: "",
      };
      // ★ 传档案id（row.partnerId），不是商机行主键（row.id）——
      //   跟进表 crm_partner_follow.partner_id 记的是档案id，传 row.id 一条都查不到
      this.followList = await opportunityApi.queryFollowByPartner(row.partnerId).catch(() => []) || [];
      this.showFollowDialog = true;
    },
    async saveFollow() {
      if (!this.$refs.followForm.validCheck()) {
        return;
      }
      await opportunityApi.saveFollow(this.followData);
      this.showFollowDialog = false;
      this.resetSearch();
    },
  },
};
</script>

<style lang="scss" scoped>
.name-link {
  color: var(--yq-primary-color, #1677ff);
  cursor: pointer;
}

/* 主副两行单元格，把两列信息压进一列 */
.cell-main {
  display: block;
  line-height: 1.5;
  text-align: inherit;
}

.cell-sub {
  display: block;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
  margin-top: 2px;
}

.follow-detail {
  max-height: 260px;
  overflow-y: auto;
  margin-bottom: 12px;
  border-bottom: 1px solid var(--yq-border-color, #ebeef5);
  padding-bottom: 8px;

  .follow-tip {
    font-size: 12px;
    color: #909399;
    margin-bottom: 8px;
  }

  .follow-item {
    padding: 6px 0;

    .follow-head {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      color: #909399;

      .follow-who {
        color: #303133;
        font-weight: 600;
      }

      .follow-time {
        margin-left: auto;
      }
    }

    .follow-content {
      font-size: 13px;
      color: #606266;
      margin-top: 4px;
      line-height: 1.5;
    }
  }
}

.num {
  font-variant-numeric: tabular-nums;
}

.bold {
  font-weight: 600;
  color: #303133;
}
</style>
